import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Table, TextLink, Tooltip } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { EyeIcon, Trash2Icon } from 'lucide-react';
import { useDetachSecurityGroupMutation } from '@/features/security-admin/hooks/useDetachSecurityGroupMutation';
import type { BackRef } from '../types/backRef';
import type { SecurityGroupRecord } from '@/features/security-admin/types/security-admin.types';

interface Props {
  data: SecurityGroupRecord[];
  backRef?: BackRef;
  productId: string;
}

export function ProductSecurityGroupDataTable({ data, backRef, productId }: Props) {
  const navigate = useNavigate();
  const detachMutation = useDetachSecurityGroupMutation(productId);

  const columns = useMemo<ColumnDef<SecurityGroupRecord>[]>(
    () => {
      const navState = backRef ? { backRef } : undefined;

      return [
        { accessorKey: 'name', header: 'Name' },
        { accessorKey: 'dealers_count', header: 'Dealers' },
        { accessorKey: 'products_count', header: 'Products' },
        { accessorKey: 'users_count', header: 'Users' },
        {
          id: 'actions',
          header: 'Actions',
          enableSorting: false,
          cell: ({ row }) => (
            <div className="flex space-x-2">
              <PermissionGuard permission={{ resource: 'security_group', action: 'read' }}>
                <Tooltip content="View security group">
                  <TextLink
                    onClick={() => navigate(`/admin/security-groups/${row.original.id}`, { state: navState })}
                    color="primary"
                    ariaLabel="View security group"
                    startIcon={<EyeIcon size={20} />}
                  />
                </Tooltip>
              </PermissionGuard>
              <PermissionGuard permission={{ resource: 'security_group', action: 'manage_products' }}>
                <Tooltip content="Detach from group">
                  <TextLink
                    onClick={() => detachMutation.mutate(row.original.id)}
                    color="error"
                    startIcon={<Trash2Icon size={18} />}
                    disabled={detachMutation.isPending}
                    ariaLabel={`Detach from ${row.original.name}`}
                  />
                </Tooltip>
              </PermissionGuard>
            </div>
          ),
        },
      ];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, backRef, detachMutation.isPending],
  );

  return <Table<SecurityGroupRecord> data={data} columns={columns} enableSorting={false} />;
}
